import Swal from 'sweetalert2';
import { branding } from '@/config/branding';
import type { IMetaTemplate } from '@/services/templates-wpp-meta/meta-wpp-template.service';
import { ehReclassificado, rotuloCategoria } from '@/services/templates-wpp-meta/meta-template-edicao';
import { seloDaCategoria } from '@/services/templates-wpp-meta/template-categories';

const corDaMarca = branding.colors.light.primary;

/** Nome do template é conteúdo do usuário; nunca entra cru no `html` do Swal. */
const escaparHtml = (texto: string) =>
    texto.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * Chave do "já fui avisado". Inclui a categoria aprovada: se a Meta reclassificar de
 * novo, o aviso volta.
 */
const chaveCiencia = (idUsuario: string | null | undefined, t: Pick<IMetaTemplate, 'id' | 'category'>) =>
    `whiz:meta-template-reclassificado-ciente:${idUsuario ?? 'anonimo'}:${t.id}:${t.category}`;

/**
 * Antes de ENVIAR um template reclassificado: confirmação explicando a mudança de
 * categoria (e de preço). Aparece uma vez por usuário e template; depois o ícone no
 * seletor basta. Devolve `false` se o usuário desistir do envio.
 */
export async function confirmarEnvioDeReclassificado(
    t: Pick<IMetaTemplate, 'id' | 'displayName' | 'category' | 'categoriaSolicitada' | 'reclassificado'>,
    idUsuario?: string | null,
): Promise<boolean> {
    if (!ehReclassificado(t)) return true;

    const chave = chaveCiencia(idUsuario, t);
    try {
        if (localStorage.getItem(chave)) return true;
    } catch {
        // Sem storage (aba privada, bloqueio): o aviso aparece de novo — é o lado seguro.
    }

    const pedida = seloDaCategoria(t.categoriaSolicitada);
    const aprovada = seloDaCategoria(t.category);
    // Mesmo selo da gestão de templates (`StatusBadge`): a classe é global em `badges.css`.
    const selo = (s: ReturnType<typeof seloDaCategoria>, categoria?: string | null) => s
        ? `<span class="status-badge bdg-tone-${s.tone}"><i class="${s.icon} status-badge__icon"></i><span>${escaparHtml(s.label)}</span></span>`
        : `<strong>${escaparHtml(categoria ?? '—')}</strong>`;

    // Padrão visual dos swals do Whiz (`components/app/swal/AbrirAtendimento.ts`): ícone
    // circular, `buttonsStyling: false`, botões `rounded-pill` e confirmar na cor da marca.
    const { isConfirmed } = await Swal.fire({
        title: '',
        html: `
            <div class="d-flex flex-column align-items-center pt-2">
                <div class="mb-3 d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle"
                    style="width: 72px; height: 72px;">
                    <i class="ri-arrow-left-right-line text-warning" style="font-size: 32px;"></i>
                </div>
                <h4 class="fw-bold mb-2 text-dark">Template reclassificado</h4>
                <p class="text-muted mb-3 px-2" style="font-size: 0.95rem; line-height: 1.5;">
                    A Meta aprovou <strong class="text-dark">${escaparHtml(t.displayName ?? '')}</strong>
                    em uma categoria diferente da que foi pedida.
                </p>
                <div class="d-flex align-items-center justify-content-center gap-2 mb-3 px-3 py-2 rounded-3 bg-light border">
                    <div class="d-flex flex-column align-items-center gap-1">
                        <span class="text-muted" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: .04em;">Pedido</span>
                        ${selo(pedida, t.categoriaSolicitada)}
                    </div>
                    <i class="ri-arrow-right-line text-muted" style="font-size: 18px; margin-top: 14px;"></i>
                    <div class="d-flex flex-column align-items-center gap-1">
                        <span class="text-muted" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: .04em;">Aprovado</span>
                        ${selo(aprovada, t.category)}
                    </div>
                </div>
                <p class="text-muted mb-0 px-2" style="font-size: 0.85rem; line-height: 1.5;">
                    Cada envio é cobrado como <strong class="text-dark">${escaparHtml(rotuloCategoria(t.category))}</strong>.
                    Este aviso não aparece de novo para este template.
                </p>
            </div>
        `,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: '<i class="ri-send-plane-line me-1"></i> Entendi, continuar',
        cancelButtonText: 'Cancelar',
        buttonsStyling: false,
        customClass: {
            popup: 'rounded-4 border-0 shadow-lg',
            confirmButton: 'btn btn-primary rounded-pill px-4 fw-semibold mx-1 shadow-sm',
            cancelButton: 'btn btn-light rounded-pill px-4 fw-semibold mx-1 border text-muted',
            actions: 'w-100 pb-4 px-2 d-flex justify-content-center flex-wrap-reverse gap-2',
        },
        didOpen: (popup) => {
            const btnConfirm = popup.querySelector('.swal2-confirm') as HTMLElement | null;
            if (btnConfirm) {
                btnConfirm.style.backgroundColor = corDaMarca;
                btnConfirm.style.borderColor = corDaMarca;
            }
        },
    });

    if (isConfirmed) {
        try { localStorage.setItem(chave, String(Date.now())); } catch { /* idem acima */ }
    }
    return isConfirmed;
}
